import React, {useState, useEffect} from 'react';

import { toKebabCase } from '../_Global/searchHelpers';
import FacetOptions from '../facet-options/jsx/FacetOptions';
import FacetTab from '../facet-tab/jsx/FacetTab';
import SortOptions from '../sort-options/jsx/SortOptions';
import SortTab from '../sort-tab/jsx/SortTab';
import ClearFacetsButton from '../clear-facets-button/jsx/ClearFacetsButton';
import useSearchStore from '../_Global/SearchStore';
import SpellingSuggestion from '../spelling-suggestion/SpellingSuggestion';

import { getElementAbsoluteYPosition } from '../../_Global/js/utils';
import useDebounce from '../_Global/useDebounce';
import useThrottle from '../_Global/useThrottle';
import useMediaQuery from '../_Global/useMediaQuery';

interface FilterConfiguration {
    facetOptions: SearchFacetConfiguration[];
    sortOptions: SortingConfiguration[];
    hasResults: boolean;
}

/**
 * The freqency (in ms) to run the scroll position checks.
 */
const scrollUpdateFrequency = 250;

/**
 * Vertical distance in pixels that a user must scroll
 * back up the page before hiding the sticky menu.
 * This is applied in a single scroll event, not cumulative.
 */
const stickyScrollUpDistance = 50;

// Used to track the document scroll positions
let lastScrollPosition = 0;
let previousScrollEndPosition = 0;

function SearchFilters({facetOptions, sortOptions, hasResults}: FilterConfiguration) {
    const spellingSuggestion = useSearchStore((state) => state.spellingSuggestion);
    const resultsSummary = useSearchStore((state) => state.resultsSummary);
    const searchFacetSelections = useSearchStore((state) => state.searchFacetSelections);
    const [filterModalActive, setFilterModalActive] = useState(false);
    const [activeFilter, setActiveFilter] = useState('');
    const [stickySectionActive, setStickySectionActive] = useState(false);
    const [totalFacetsSelected, setTotalFacetsSelected] = useState(0);
    const debounceCallback = useDebounce(onPageScrollEnd, scrollUpdateFrequency);
    const throttledCallback = useThrottle(onPageScroll, scrollUpdateFrequency);
    const isLargeDevice = useMediaQuery("screen and (min-width: 1200px)");

    const summaryDetails = (
        <p className="body-default">
            Showing <span className="vertical-divider body-size-sm" role="presentation"></span> {resultsSummary.currentSetStart}-{resultsSummary.currentSetEnd} of{' '}
            {resultsSummary.totalMatching} results
        </p>
    );

    /**
     * Action to take when the component is created or destroyed
     */
    useEffect(() => {
        // Listen for the scrolling event for the sticky filters
        document.addEventListener('scroll', throttledCallback);

        // Catch the scroll end event for the sticky filters
        document.addEventListener('scroll', debounceCallback);

        return () => {
            document.removeEventListener('scroll', throttledCallback);
            document.removeEventListener('scroll', debounceCallback);
        }
    }, []);

    /**
     * Update the count of total facets that have been selected
     */
    useEffect(() => {
        const selectedFacetValues = Object.values(searchFacetSelections);
        let totalFacetsSelected = 0;

        for (let i = 0, j = selectedFacetValues.length; i < j; ++i) {
            totalFacetsSelected += selectedFacetValues[i].length;
        }

        setTotalFacetsSelected(totalFacetsSelected);
    }, [searchFacetSelections]);

    /**
     * Handle the keyboard event for the filters popup
     * Removing on modal close or unmount to clear out duplicate events
     */
    useEffect(() => {
        if (filterModalActive) {
            document.addEventListener('keydown', handleDialogCloseKeyboardEvt);
            return () => document.removeEventListener('keydown', handleDialogCloseKeyboardEvt);
        } else {
            document.removeEventListener('keydown', handleDialogCloseKeyboardEvt);
        }
    }, [filterModalActive]);


    /**
     * React to changes to the search filters "sticky" state being toggled
     */
    useEffect(() => {
        // Reset the filter tabs to all be closed
        setActiveFilter('');

    }, [stickySectionActive]);




    const handleDialogCloseKeyboardEvt = (evt: KeyboardEvent) => {
        if (evt.key === 'Escape') {
            evt.stopPropagation();
            setFilterModalActive(false);
        }
    };

    /**
     * respond to the page scrolling event
     */
    function onPageScroll() {
        // Selector for targeting the element that should be sticky
        const stickyParentQuerySelector = '[data-role="filters-sticky-parent"]';

        // Capture the most recent scroll position
        const recentScrollPosition = window.scrollY;

        // Capture the current position of the non-sticky parent element
        // This may change at different viewport widths
        const currentNonStickySectionPosition = getElementAbsoluteYPosition(stickyParentQuerySelector);

        if (recentScrollPosition > lastScrollPosition) {
            // User is scrolling down

            // Check if user has scrolled past the "default" (when it's not sticky) sticky section position
            if (!stickySectionActive && recentScrollPosition > currentNonStickySectionPosition) {
                // Add the sticky functionality
                setStickySectionActive(true);
            }
        } else {
            // User is scrolling up

            // Check if user has scrolled past the "default" (when it's not sticky) sticky section position
            if (stickySectionActive && recentScrollPosition <= currentNonStickySectionPosition) {
                // Remove the sticky functionality
                setStickySectionActive(false);
            }

            // Check if user has scrolled up a minimum threshold in the current scroll action
            if (recentScrollPosition < previousScrollEndPosition - stickyScrollUpDistance) {
                // Remove the sticky functionality
                setStickySectionActive(false);
            }
        }

        // Store the most recent scroll position
        lastScrollPosition = recentScrollPosition;
    }

    /**
     * capture the scroll position of the page when
     * the user has finished scrolling
     */
    function onPageScrollEnd() {
        previousScrollEndPosition = window.scrollY;

        // Trigger a final check for the scroll layout
        onPageScroll();
    }

    return (
        <>
            <div
                className="search-filter-modal__controls"
                data-role={ !isLargeDevice ? 'filters-sticky-parent' : '' }
                >
                <button
                    className={`search-filter-modal__sticky-button button button--tab body-size-xl${filterModalActive ? ' icon__expand--active' : ''}${stickySectionActive ? ' is-sticky-section' : ''}`}
                    type="button"
                    onClick={() => setFilterModalActive(true)}
                    >
                    <div className="icon__expand-container">
                        <svg className="icon__expand-horizontal">
                            <use xlinkHref="#minus"></use>
                        </svg>
                        <svg className="icon__expand-vertical">
                            <use xlinkHref="#minus"></use>
                        </svg>
                    </div>
                    <span className="sr-only">Apply search </span>
                    <span className="button__label">Filters{ totalFacetsSelected !== 0 ? ` (${totalFacetsSelected})` : null }</span>
                </button>
                {/* A placeholder element to prevent layout shift with sticky menu */}
                <span className="search-filter-modal__sticky-placeholder button button--tab body-size-xl" aria-hidden="true">
                    <span className="button__label">&nbsp;</span>
                </span>
            </div>

            <div
                aria-labelledby="search-filters"
                aria-modal="true"
                className={`search-filter-modal__wrapper${filterModalActive ? ' search-filter-modal--active' : ''}`}
                data-role={ isLargeDevice ? 'filters-sticky-parent' : '' }
                role="dialog"
                >
                <button className="search-filter-modal__hidden-button-close" type="button" tabIndex={-1} aria-hidden="true" onClick={() => setFilterModalActive(false)}>
                    <span className="sr-only">Close search filters</span>
                </button>

                <div className={`search-filter-tabs__wrapper${stickySectionActive ? ' is-sticky-section' : ''}`}>
                    <div className="search-filter-modal__header">
                        <h2 id="search-filters" className="body-size-xl">
                            Search filters{ totalFacetsSelected !== 0 ? ` (${totalFacetsSelected})` : null }
                        </h2>

                        <button className="search-filter-modal__button-close" type="button" onClick={() => setFilterModalActive(false)}>
                            <svg>
                                <use xlinkHref="#cross"></use>
                            </svg>
                            <span className="sr-only">Close search filters</span>
                        </button>
                    </div>

                    <div className="search-filter-modal__content-scroller" role="tablist" aria-labelledby="search-filters">
                        <div className="search-filter-tabs__tab-list" role="tablist" aria-labelledby="search-filters">
                            {facetOptions.map((facetData) => {
                                return (
                                    <FacetTab
                                        key={toKebabCase(facetData.queryStringParamName)}
                                        facetTitle={facetData.name}
                                        facetKey={facetData.name}
                                        facetParamName={facetData.queryStringParamName}
                                        isActive={activeFilter === facetData.name}
                                        onToggle={setActiveFilter}
                                    />
                                );
                            })}

                            <SortTab label="Sort by" isActive={activeFilter === 'Sort by'} onToggle={setActiveFilter} />
                        </div>

                        {facetOptions.map((facetData) => {
                            return (
                                <FacetOptions
                                    key={toKebabCase(facetData.queryStringParamName)}
                                    facetTitle={facetData.name}
                                    facetKey={facetData.name}
                                    facetParamName={facetData.queryStringParamName}
                                    facetType={facetData.type}
                                    showCount={facetData.showResultCount}
                                    isActive={activeFilter === facetData.name}
                                    onToggle={setActiveFilter}
                                />
                            );
                        })}

                        <SortOptions label="Sort by" selectOptions={sortOptions} isActive={activeFilter === 'Sort by'} onToggle={setActiveFilter} />
                    </div>

                    <div className="search-filter-modal__filters-reset">
                        <ClearFacetsButton />
                    </div>
                </div>
                {/* A placeholder element to prevent layout shift with sticky tabs */}
                <span className="search-filter-tabs__sticky-placeholder button button--tab body-size-xl" aria-hidden="true">
                    <span className="button__label">&nbsp;</span>
                </span>
            </div>

            <div className="search-filter__summary">
                {summaryDetails}
                <div className="search-filter__filters-reset">
                    <ClearFacetsButton />
                </div>
            </div>

            {hasResults && <SpellingSuggestion suggestion={spellingSuggestion} />}
        </>
    );
}

export default React.memo(SearchFilters);
